/**
 * EmailTaskModal - Send task via email with multiple delivery options.
 *
 * Options: Gmail, Outlook, Preview with Images (new window/PDF), Download Images, Download HTML.
 * Collects images from linked issues, renders thermal ROIs onto images via offscreen canvas,
 * and embeds them inline with each linked issue in the HTML work order.
 *
 * For Gmail/Outlook: images are auto-downloaded so the user can attach them manually.
 */

import { useState } from 'react';
import type { Task, Issue } from '@/types/tasks';
import type { ThermalRoi, ThermalPoint } from '@/components/editor/thermal/thermalTypes';
import { resolveImageSrc } from '@/utils/photoBlobs';
import { resolveSignedUrl } from '@/services/signedAssetUrl';
import './EmailTaskModal.css';

// ─── Props ────────────────────────────────────────────────────────────────────

interface EmailTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  tasks: Task[];
  allIssues: Issue[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface CollectedImage {
  issueId: string;
  title: string;
  url: string;
  rois: ThermalRoi[];
  points: ThermalPoint[];
  isLocal: boolean;
  isThermal: boolean;
}

/** Convert a blob: or data: URL to a base64 data URL. */
function urlToBase64(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = url;
  });
}

/** Draw an image with thermal ROI/point annotations onto an offscreen canvas, return base64. */
function drawImageWithROIs(
  imgUrl: string,
  rois: ThermalRoi[],
  points: ThermalPoint[]
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);

      // Draw ROIs
      ctx.lineWidth = 2;
      ctx.font = '14px Arial';
      for (let i = 0; i < rois.length; i++) {
        const r = rois[i];
        const rx0 = Math.min(r.x0, r.x1);
        const ry0 = Math.min(r.y0, r.y1);
        const rw = Math.abs(r.x1 - r.x0);
        const rh = Math.abs(r.y1 - r.y0);

        // Blue rectangle
        ctx.strokeStyle = '#1d4ed8';
        ctx.fillStyle = 'rgba(29, 78, 216, 0.12)';
        ctx.beginPath();
        ctx.rect(rx0, ry0, rw, rh);
        ctx.stroke();
        ctx.fill();

        // Label
        const label =
          r.stats && typeof r.stats.max_c === 'number'
            ? `ROI${i + 1} max ${r.stats.max_c.toFixed(2)}\u00B0C`
            : `ROI${i + 1}`;
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        const tw = ctx.measureText(label).width;
        ctx.fillRect(rx0 + 2, ry0 + 2, tw + 8, 18);
        ctx.fillStyle = '#111';
        ctx.fillText(label, rx0 + 6, ry0 + 16);

        // Hotspot crosshair
        if (r.stats && typeof r.stats.max_x === 'number') {
          const hx = r.stats.max_x;
          const hy = r.stats.max_y;
          const hs = 10;
          ctx.lineWidth = 3;
          ctx.strokeStyle = '#fff';
          ctx.beginPath();
          ctx.moveTo(hx - hs, hy);
          ctx.lineTo(hx + hs, hy);
          ctx.moveTo(hx, hy - hs);
          ctx.lineTo(hx, hy + hs);
          ctx.stroke();
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = '#ff0000';
          ctx.beginPath();
          ctx.moveTo(hx - hs, hy);
          ctx.lineTo(hx + hs, hy);
          ctx.moveTo(hx, hy - hs);
          ctx.lineTo(hx, hy + hs);
          ctx.stroke();
        }
      }

      // Draw points
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        const px = p.x;
        const py = p.y;
        const s = 10;
        // White outline
        ctx.lineWidth = 4;
        ctx.strokeStyle = '#fff';
        ctx.beginPath();
        ctx.moveTo(px - s, py);
        ctx.lineTo(px + s, py);
        ctx.moveTo(px, py - s);
        ctx.lineTo(px, py + s);
        ctx.stroke();
        // Red cross
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#cc0000';
        ctx.beginPath();
        ctx.moveTo(px - s, py);
        ctx.lineTo(px + s, py);
        ctx.moveTo(px, py - s);
        ctx.lineTo(px, py + s);
        ctx.stroke();
        // Dot
        ctx.fillStyle = '#cc0000';
        ctx.beginPath();
        ctx.arc(px, py, 3, 0, Math.PI * 2);
        ctx.fill();
        // Label
        const label =
          p.value_c == null ? `P${i + 1}` : `P${i + 1} ${p.value_c.toFixed(2)}\u00B0C`;
        const tw2 = ctx.measureText(label).width;
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.fillRect(px + 12, py - 20, tw2 + 8, 20);
        ctx.fillStyle = '#cc0000';
        ctx.fillText(label, px + 16, py - 4);
      }

      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = imgUrl;
  });
}

/** Collect images from linked issues, keyed by issueId. */
function collectImages(tasks: Task[], allIssues: Issue[]): CollectedImage[] {
  const images: CollectedImage[] = [];
  const linkedIds = new Set<string>();
  for (const t of tasks) {
    for (const id of t.linkedIssueIds) linkedIds.add(id);
  }

  for (const issue of allIssues) {
    if (!linkedIds.has(issue.issueId)) continue;
    if (!issue.image) continue;

    const resolved = resolveImageSrc(issue.image);
    if (!resolved) continue;

    const isLocal = resolved.startsWith('blob:') || resolved.startsWith('data:');
    const rois = issue.thermalData?.rois ?? [];
    const points = issue.thermalData?.points ?? [];
    const isThermal = rois.length > 0 || points.length > 0;

    images.push({
      issueId: issue.issueId,
      title: `${issue.category} - ${issue.indType}`,
      url: resolved,
      rois,
      points,
      isLocal,
      isThermal,
    });
  }

  return images;
}

/** Build plain text email body. If attachImages is true, adds a note about attaching downloaded images. */
function buildPlainText(tasks: Task[], allIssues: Issue[], attachImages = false): string {
  const lines: string[] = [];
  const isBatch = tasks.length > 1;

  if (attachImages) {
    lines.push('** Please attach the downloaded image(s) to this email **');
    lines.push('');
  }

  if (isBatch) {
    lines.push('WORK ORDER');
    lines.push(`Tasks: ${tasks.length}`);
    lines.push('');
  }

  for (const task of tasks) {
    lines.push(`TASK: ${task.title}`);
    lines.push(`Status: ${task.status}`);
    lines.push(`Priority: ${task.priority}`);
    lines.push('');

    if (task.schedule.start) {
      const start = new Date(task.schedule.start).toLocaleDateString();
      const end = task.schedule.end
        ? new Date(task.schedule.end).toLocaleDateString()
        : start;
      lines.push(`Scheduled: ${start} - ${end}`);
    }
    if (task.assignees.length > 0) {
      lines.push(`Assigned to: ${task.assignees.map((a) => a.userId).join(', ')}`);
    }
    lines.push('');
    lines.push('DESCRIPTION:');
    lines.push(task.description || '(No description)');
    lines.push('');

    // Linked issues
    const linked = allIssues.filter((i) => task.linkedIssueIds.includes(i.issueId));
    if (linked.length > 0) {
      lines.push('LINKED ISSUES:');
      for (const issue of linked) {
        const path = [issue.assetPath.station, issue.assetPath.inverter, issue.assetPath.dccb, issue.assetPath.string, issue.assetPath.module]
          .filter(Boolean)
          .join(' > ');
        lines.push(`  - ${issue.category} (${issue.severity})`);
        if (path) lines.push(`    Location: ${path}`);
      }
      lines.push('');
    }

    if (task.checklist.length > 0) {
      lines.push('CHECKLIST:');
      for (const item of task.checklist) {
        lines.push(`  [ ] ${item}`);
      }
      lines.push('');
    }

    if (isBatch) {
      lines.push('---');
      lines.push('');
    }
  }

  lines.push('---');
  lines.push('Sent from SoleadLab Inspection Editor');
  return lines.join('\n');
}

/**
 * Escape a value for safe interpolation into the work-order HTML. The output is
 * rendered via `win.document.write` in a blank same-origin window, so unescaped
 * user text (task titles/descriptions, issue category — which can be a custom
 * indication name — asset-path fragments, assignees, checklist items) would be
 * live markup / executable script. Escapes both element-content and quoted-
 * attribute contexts.
 */
export function escapeHtml(val: unknown): string {
  return String(val ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Build HTML email body with images inline next to their linked issues.
 *  Exported for the XSS-escaping regression test. */
export function buildHTML(tasks: Task[], allIssues: Issue[], imageMap: Map<string, string>): string {
  let html = `<div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto;">`;
  html += `<div style="background: linear-gradient(135deg, #7c3aed, #6d28d9); color: #fff; padding: 20px 24px; border-radius: 8px 8px 0 0;">`;
  html += `<h1 style="margin: 0; font-size: 20px;">SoleadLab Work Order</h1>`;
  html += `<p style="margin: 4px 0 0; font-size: 13px; opacity: 0.85;">${tasks.length} task${tasks.length > 1 ? 's' : ''} \u2022 ${new Date().toLocaleDateString()}</p>`;
  html += `</div>`;

  for (const task of tasks) {
    const prioColors: Record<string, string> = { P1: '#dc2626', P2: '#f59e0b', P3: '#3b82f6', P4: '#6b7280' };
    html += `<div style="border: 1px solid #e5e7eb; border-top: 0; padding: 20px 24px;">`;
    html += `<h2 style="margin: 0 0 12px; font-size: 16px; color: #1f2937;">`;
    html += `<span style="display: inline-block; background: ${prioColors[task.priority] || '#6b7280'}; color: #fff; padding: 2px 8px; border-radius: 4px; font-size: 11px; margin-right: 8px;">${escapeHtml(task.priority)}</span>`;
    html += `${escapeHtml(task.title)}</h2>`;

    html += `<table style="margin-bottom: 12px; font-size: 13px;">`;
    html += `<tr><td style="padding: 3px 12px 3px 0; color: #6b7280;">Status:</td><td style="font-weight: 600;">${escapeHtml(task.status)}</td></tr>`;
    if (task.schedule.start) {
      html += `<tr><td style="padding: 3px 12px 3px 0; color: #6b7280;">Scheduled:</td><td>${new Date(task.schedule.start).toLocaleDateString()}${task.schedule.end ? ' - ' + new Date(task.schedule.end).toLocaleDateString() : ''}</td></tr>`;
    }
    if (task.assignees.length > 0) {
      html += `<tr><td style="padding: 3px 12px 3px 0; color: #6b7280;">Assigned:</td><td>${escapeHtml(task.assignees.map((a) => a.userId).join(', '))}</td></tr>`;
    }
    html += `</table>`;

    if (task.description) {
      html += `<p style="color: #4b5563; font-size: 13px; white-space: pre-wrap; margin: 0 0 12px;">${escapeHtml(task.description)}</p>`;
    }

    // Linked issues with inline images
    const linked = allIssues.filter((i) => task.linkedIssueIds.includes(i.issueId));
    if (linked.length > 0) {
      html += `<h3 style="color: #374151; font-size: 13px; margin: 12px 0 8px;">Linked Issues</h3>`;
      for (const issue of linked) {
        const path = [issue.assetPath.station, issue.assetPath.inverter, issue.assetPath.dccb, issue.assetPath.string, issue.assetPath.module]
          .filter(Boolean)
          .join(' \u203A ');
        const sevColors: Record<string, string> = { Critical: '#dc2626', Major: '#f59e0b', Minor: '#3b82f6', Info: '#6b7280' };

        html += `<div style="border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; margin-bottom: 10px; background: #fafafa;">`;
        html += `<strong style="font-size: 13px;">${escapeHtml(issue.category)}</strong>`;
        html += ` <span style="color: ${sevColors[issue.severity] || '#6b7280'}; font-size: 12px;">(${escapeHtml(issue.severity)})</span>`;
        if (path) html += `<br><small style="color: #9ca3af;">${escapeHtml(path)}</small>`;

        // Thermal stats
        if (issue.thermalData?.rois) {
          for (const roi of issue.thermalData.rois) {
            if (roi.stats && typeof roi.stats.max_c === 'number') {
              html += `<br><small style="color: #dc2626;">\u0394T ${roi.stats.delta_t?.toFixed(1) ?? '--'}\u00B0C \u2022 max ${roi.stats.max_c.toFixed(1)}\u00B0C \u2022 avg ${roi.stats.avg_c?.toFixed(1) ?? '--'}\u00B0C</small>`;
            }
          }
        }

        // Inline thermal image for this issue
        const imgData = imageMap.get(issue.issueId);
        if (imgData) {
          html += `<div style="margin-top: 10px;"><img src="${imgData}" style="max-width: 100%; border-radius: 4px; border: 1px solid #e5e7eb; display: block;" /></div>`;
        }

        html += `</div>`;
      }
    }

    if (task.checklist.length > 0) {
      html += `<h3 style="color: #374151; font-size: 13px; margin: 12px 0 6px;">Checklist</h3>`;
      html += `<ul style="list-style: none; padding: 0; margin: 0; font-size: 12px;">`;
      for (const item of task.checklist) {
        html += `<li style="padding: 3px 0;">\u2610 ${escapeHtml(item)}</li>`;
      }
      html += `</ul>`;
    }

    html += `</div>`;
  }

  // Footer
  html += `<div style="border: 1px solid #e5e7eb; border-top: 0; padding: 16px 24px; border-radius: 0 0 8px 8px; background: #f9fafb;">`;
  html += `<p style="color: #9ca3af; font-size: 11px; margin: 0;">Sent from SoleadLab Inspection Editor \u2022 ${new Date().toLocaleString()}</p>`;
  html += `</div></div>`;

  return html;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function EmailTaskModal({
  isOpen,
  onClose,
  tasks,
  allIssues,
}: EmailTaskModalProps) {
  const [processing, setProcessing] = useState(false);

  if (!isOpen || tasks.length === 0) return null;

  const subject =
    tasks.length === 1
      ? `Task: ${tasks[0].title} [${tasks[0].status}]`
      : `Work Order: ${tasks.length} tasks`;

  const images = collectImages(tasks, allIssues);
  const hasImages = images.length > 0;

  /** Process all collected images → Map<issueId, base64 data URL>. */
  // Cloud-hosted images go through resolveSignedUrl first: the signed URL has
  // an HMAC token in the query string so the canvas (crossOrigin='anonymous',
  // no cookies) can still authenticate. Local blob:/data: URLs are passed
  // through untouched — resolveSignedUrl is a no-op for them.
  const processImages = async (): Promise<Map<string, string>> => {
    const result = new Map<string, string>();
    for (const img of images) {
      try {
        const url = img.isLocal ? img.url : await resolveSignedUrl(img.url);
        const dataUrl = img.isThermal
          ? await drawImageWithROIs(url, img.rois, img.points)
          : await urlToBase64(url);
        result.set(img.issueId, dataUrl);
      } catch (e) {
        console.warn('[Email] Failed to process image:', e);
      }
    }
    return result;
  };

  /** Download processed images as individual PNG files. */
  const downloadImageFiles = async (imageMap: Map<string, string>) => {
    let idx = 0;
    for (const img of images) {
      const dataUrl = imageMap.get(img.issueId);
      if (!dataUrl) continue;
      idx++;
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `task-image-${idx}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // ── Gmail: download images + open compose ────────────────────────────
  const handleGmail = async () => {
    setProcessing(true);
    try {
      if (hasImages) {
        const imageMap = await processImages();
        await downloadImageFiles(imageMap);
        // Brief delay so downloads register before opening compose
        await new Promise((r) => setTimeout(r, 500));
      }
      const plainText = buildPlainText(tasks, allIssues, hasImages);
      const url = `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(plainText)}`;
      window.open(url, '_blank');
    } finally {
      setProcessing(false);
    }
    onClose();
  };

  // ── Outlook: download images + open compose ──────────────────────────
  const handleOutlook = async () => {
    setProcessing(true);
    try {
      if (hasImages) {
        const imageMap = await processImages();
        await downloadImageFiles(imageMap);
        await new Promise((r) => setTimeout(r, 500));
      }
      const plainText = buildPlainText(tasks, allIssues, hasImages);
      const url = `https://outlook.live.com/mail/0/deeplink/compose?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(plainText)}`;
      window.open(url, '_blank');
    } finally {
      setProcessing(false);
    }
    onClose();
  };

  // ── Preview with images (new window, user can print to PDF) ──────────
  const handlePreview = async () => {
    setProcessing(true);
    try {
      const imageMap = await processImages();
      const html = buildHTML(tasks, allIssues, imageMap);
      const fullPage = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(subject)}</title></head><body style="margin: 20px; background: #f3f4f6;">${html}</body></html>`;
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(fullPage);
        win.document.close();
      }
    } finally {
      setProcessing(false);
    }
    onClose();
  };

  // ── Download individual image files ──────────────────────────────────
  const handleDownloadImages = async () => {
    setProcessing(true);
    try {
      const imageMap = await processImages();
      await downloadImageFiles(imageMap);
    } finally {
      setProcessing(false);
    }
    onClose();
  };

  // ── Download full HTML file ──────────────────────────────────────────
  const handleDownloadHTML = async () => {
    setProcessing(true);
    try {
      const imageMap = await processImages();
      const html = buildHTML(tasks, allIssues, imageMap);
      const fullPage = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(subject)}</title></head><body style="margin: 20px; background: #f3f4f6;">${html}</body></html>`;
      const blob = new Blob([fullPage], { type: 'text/html' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `work-order-${new Date().toISOString().slice(0, 10)}.html`;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setProcessing(false);
    }
    onClose();
  };

  return (
    <div className="email-modal-overlay" onClick={onClose}>
      <div className="email-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="email-modal__title">Send {tasks.length > 1 ? 'Work Order' : 'Task'} via Email</h3>

        {/* Image preview thumbnails */}
        {hasImages && (
          <div className="email-modal__images">
            <span className="email-modal__images-label">{images.length} image(s) will be included</span>
            <div className="email-modal__images-grid">
              {images.map((img, i) => (
                <img key={i} className="email-modal__thumb" src={img.url} alt={img.title} />
              ))}
            </div>
          </div>
        )}

        <p className="email-modal__hint">Choose how to send:</p>

        <div className="email-modal__options">
          <button className="email-modal__option" onClick={handleGmail} disabled={processing}>
            <span className="email-modal__option-icon">&#x1F4E8;</span>
            {processing ? 'Processing...' : hasImages ? 'Open in Gmail (images will download)' : 'Open in Gmail'}
          </button>
          <button className="email-modal__option" onClick={handleOutlook} disabled={processing}>
            <span className="email-modal__option-icon">&#x1F4E7;</span>
            {processing ? 'Processing...' : hasImages ? 'Open in Outlook (images will download)' : 'Open in Outlook'}
          </button>
          <button
            className="email-modal__option email-modal__option--primary"
            onClick={handlePreview}
            disabled={processing}
          >
            <span className="email-modal__option-icon">&#x1F5BC;&#xFE0F;</span>
            {processing ? 'Processing...' : 'Preview with Images (save as PDF)'}
          </button>
          {hasImages && (
            <button className="email-modal__option" onClick={handleDownloadImages} disabled={processing}>
              <span className="email-modal__option-icon">&#x1F4BE;</span>
              {processing ? 'Processing...' : `Download Images (${images.length})`}
            </button>
          )}
          <button className="email-modal__option" onClick={handleDownloadHTML} disabled={processing}>
            <span className="email-modal__option-icon">&#x1F4C4;</span>
            {processing ? 'Processing...' : 'Download as HTML file'}
          </button>
        </div>

        <button className="email-modal__cancel" onClick={onClose} disabled={processing}>
          Cancel
        </button>
      </div>
    </div>
  );
}
